import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { COLORS } from '../../lib/constants';

export default function MyBookings() {
  const { profile } = useAuth();
  const { isRtl } = useLang();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (!profile || !profile.id) return;
    loadBookings();
  }, [profile]);

  const loadBookings = async () => {
    const { data } = await supabase
      .from('bookings')
      .select('*, businesses(name_ar, name_en, logo_url, lat, lng, city, district), services(name_ar, name_en)')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });
    setBookings(data || []);
    setLoading(false);
  };

  const submitReview = async () => {
    if (!reviewBooking) return;
    setReviewSaving(true);
    const { error } = await supabase.from('reviews').insert({
      booking_id: reviewBooking.id,
      user_id: profile.id,
      business_id: reviewBooking.business_id,
      staff_id: reviewBooking.staff_id,
      customer_name: profile.full_name,
      rating: reviewRating,
      comment: reviewComment,
    });
    setReviewSaving(false);
    if (error) {
      if (error.code === '23505') {
        alert(isRtl ? 'لقد قيّمت هذا الحجز مسبقاً' : 'You already reviewed this booking');
      } else {
        alert(isRtl ? 'تعذر إرسال التقييم، حاول مرة أخرى' : 'Could not submit review, please try again');
      }
      return;
    }
    setReviewBooking(null);
    setReviewComment('');
    setReviewRating(5);
  };

  const STATUS_COLOR = { pending: '#F59E0B', confirmed: '#3B82F6', completed: COLORS.success, cancelled: COLORS.error };
  const STATUS_AR = { pending: 'قيد الانتظار', confirmed: 'مؤكد', completed: 'مكتمل', cancelled: 'ملغي' };
  const STATUS_EN = { pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled' };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm(isRtl ? 'هل أنت متأكد من إلغاء هذا الحجز؟' : 'Are you sure you want to cancel this booking?')) return;
    setCancellingId(bookingId);
    const { data, error } = await supabase.rpc('cancel_my_booking', { p_booking_id: bookingId });
    setCancellingId(null);
    if (error || !data || data.success === false) {
      alert(isRtl ? 'تعذر إلغاء الحجز، حاول مرة أخرى' : 'Could not cancel the booking, please try again');
      return;
    }
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'cancelled' } : b));
  };

  const filtered = filter === 'all' ? bookings : bookings.filter(function(b) { return b.status === filter; });

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr', paddingBottom: 80 }}>

      {reviewBooking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 380 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 6 }}>{isRtl ? 'كيف كانت تجربتك؟' : 'How was your experience?'}</div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>{isRtl ? (reviewBooking.businesses && reviewBooking.businesses.name_ar) : (reviewBooking.businesses && (reviewBooking.businesses.name_en || reviewBooking.businesses.name_ar))}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
              {[1,2,3,4,5].map(function(star) {
                return (
                  <button key={star} onClick={function() { setReviewRating(star); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 36, color: star <= reviewRating ? '#F59E0B' : '#DDD' }}>★</button>
                );
              })}
            </div>
            <textarea value={reviewComment} onChange={function(e) { setReviewComment(e.target.value); }} placeholder={isRtl ? 'شاركنا تجربتك...' : 'Share your experience...'} style={{ width: '100%', padding: '12px 14px', border: '1px solid ' + COLORS.border, borderRadius: 12, fontSize: 13, fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr', outline: 'none', resize: 'none', height: 80, boxSizing: 'border-box', marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={function() { setReviewBooking(null); }} style={{ flex: 1, padding: '11px 0', background: COLORS.bg, border: '1px solid ' + COLORS.border, borderRadius: 12, cursor: 'pointer', fontSize: 14, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'إلغاء' : 'Cancel'}</button>
              <button onClick={submitReview} disabled={reviewSaving} style={{ flex: 1, padding: '11px 0', background: 'linear-gradient(135deg, ' + COLORS.primary + ', ' + COLORS.primaryDark + ')', border: 'none', borderRadius: 12, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                {reviewSaving ? '...' : (isRtl ? 'إرسال ⭐' : 'Submit ⭐')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', padding: '20px 16px 0', borderBottom: '1px solid ' + COLORS.border }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, marginBottom: 16 }}>{isRtl ? 'حجوزاتي' : 'My Bookings'}</div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12 }}>
          {[['all','الكل','All'],['pending','انتظار','Pending'],['confirmed','مؤكدة','Confirmed'],['completed','مكتملة','Completed'],['cancelled','ملغية','Cancelled']].map(function(item) {
            var val = item[0], ar = item[1], en = item[2];
            return (
              <button key={val} onClick={function() { setFilter(val); }} style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20, border: '1.5px solid ' + (filter === val ? COLORS.primary : COLORS.border), background: filter === val ? COLORS.primaryLight : '#fff', color: filter === val ? COLORS.primary : COLORS.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}>
                {isRtl ? ar : en}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {loading && <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>{isRtl ? 'جارٍ التحميل...' : 'Loading...'}</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
            <div style={{ color: COLORS.textMuted, fontSize: 14 }}>{isRtl ? 'لا توجد حجوزات' : 'No bookings found'}</div>
          </div>
        )}

        {filtered.map(function(b) {
          return (
            <div key={b.id} style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: '1px solid ' + COLORS.border }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {b.businesses && b.businesses.logo_url ? <img src={b.businesses.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🏪'}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{isRtl ? (b.businesses && b.businesses.name_ar) : (b.businesses && (b.businesses.name_en || b.businesses.name_ar))}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? (b.services && b.services.name_ar) : (b.services && (b.services.name_en || b.services.name_ar))}</div>
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 10, background: (STATUS_COLOR[b.status] || '#999') + '20', color: STATUS_COLOR[b.status] || '#999' }}>
                  {isRtl ? STATUS_AR[b.status] : STATUS_EN[b.status]}
                </span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: COLORS.textMuted }}>📅 {b.booking_date}</span>
                <span style={{ fontSize: 12, color: COLORS.textMuted }}>🕐 {b.booking_time ? b.booking_time.slice(0,5) : ''}</span>
                <span style={{ fontSize: 12, color: COLORS.primary, fontWeight: 700 }}>💰 {b.price} {isRtl ? 'ر.س' : 'SAR'}</span>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {b.businesses && b.businesses.lat && b.businesses.lng && (
                  <a href={'https://maps.google.com/?q=' + b.businesses.lat + ',' + b.businesses.lng} target="_blank" rel="noreferrer" style={{ flex: 1, padding: '8px 0', background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 10, color: '#2E7D32', fontWeight: 700, fontSize: 12, textDecoration: 'none', textAlign: 'center', fontFamily: 'Cairo, sans-serif' }}>
                    📍 {isRtl ? 'الموقع' : 'Location'}
                  </a>
                )}
                {(b.status === 'pending' || b.status === 'confirmed') && (
                  <button onClick={function() { cancelBooking(b.id); }} disabled={cancellingId === b.id} style={{ flex: 1, padding: '8px 0', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, color: COLORS.error, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                    {cancellingId === b.id ? '...' : (isRtl ? '✕ إلغاء الحجز' : '✕ Cancel')}
                  </button>
                )}
                {b.status === 'completed' && (
                  <button onClick={function() { setReviewBooking(b); }} style={{ flex: 1, padding: '8px 0', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 10, color: '#D97706', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                    ⭐ {isRtl ? 'قيّم' : 'Review'}
                  </button>
                )}
                <a href="https://wa.me/966500748456" target="_blank" rel="noreferrer" style={{ flex: 1, padding: '8px 0', background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 10, color: '#2E7D32', fontWeight: 700, fontSize: 12, textDecoration: 'none', textAlign: 'center', fontFamily: 'Cairo, sans-serif' }}>
                  💬 {isRtl ? 'الدعم' : 'Support'}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
